<template>
  <div class="m-report">
    <div class="navbar">
      <button v-if="isLoggedIn" class="back" @click="goBack">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"
             stroke-linejoin="round">
          <path d="M15 19l-7-7 7-7"/>
        </svg>
      </button>
      <h1>个人报告</h1>
    </div>

    <div v-if="loading" class="m-loading"><span class="spin"></span>
      <p>加载中…</p></div>
    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button class="btn" @click="onRetry" :disabled="loading">重试</button>
    </div>
    <div v-else id="pdf-container" class="pdf-container"></div>
  </div>
</template>

<script>
import mobileRem from '@/commonJs/mobileRem';
import pub from '@/service/public';
import Pdfh5 from 'pdfh5';
import 'pdfh5/css/pdfh5.css';
import config from '@/config';

export default {
  name: 'MReport',
  mixins: [mobileRem],
  data() {
    return {
      loading: false,
      error: '',
      pdfh5: null,
      isLoggedIn: false
    };
  },
  computed: {
    orderId() {
      return this.$route.query.id || '';
    }
  },
  mounted() {
    this.isLoggedIn = pub.isLoggedIn();
    this.fetchPdfUrl();
  },
  watch: {
    '$route'() {
      this.isLoggedIn = pub.isLoggedIn();
    }
  },
  beforeDestroy() {
    if (this.pdfh5) {
      this.pdfh5.destroy();
      this.pdfh5 = null;
    }
  },
  methods: {
    // 手动重试时重置自动重试标记
    onRetry() {
      this._autoRetried = false;
      this.fetchPdfUrl();
    },
    async fetchPdfUrl() {
      const q = this.$route.query;
      // 使用 orderUUID 获取报告
      const orderUUID = q.id;
      if (!orderUUID) {
        this.error = '订单ID不存在';
        return;
      }

      this.loading = true;
      this.error = '';

      try {
        // 免登录分享页首次打开时 config.json 可能尚未加载完，
        // 同步取值会拿到 undefined/旧地址导致跨域报错，必须先等配置就绪
        const cfg = await config.getConfig();
        const pdfApiRoot = cfg.PDF_API_ROOT || cfg.API_ROOT;
        if (!pdfApiRoot) {
          this.error = '服务配置缺失';
          this.loading = false;
          return;
        }
        const url = `${pdfApiRoot}/Record/GetReport?strOrderUUID=${encodeURIComponent(orderUUID)}`;

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('获取报告失败');
        }
        const data = await response.json();

        this.loading = false;
        if (data && data.code === pub.backCode.SUCCESS) {
          const list = data.data && data.data.pageList;
          if (list && list.length > 0) {
            const reportUrl = list[0].reportUrl;
            if (reportUrl) {
              this.initPdfh5(reportUrl);
            } else {
              this.error = '该记录暂无报告';
            }
          } else {
            this.error = '未找到该记录';
          }
        } else {
          this.error = (data && data.message) || '获取PDF地址失败';
        }
      } catch (err) {
        this.loading = false;
        console.error('获取PDF失败：', err);
        // 首次加载偶发失败（如配置/网络时序），自动重试一次
        if (!this._autoRetried) {
          this._autoRetried = true;
          this.fetchPdfUrl();
          return;
        }
        this.error = '获取PDF失败，请稍后重试';
      }
    },
    initPdfh5(url) {
      this.$nextTick(() => {
        const container = document.getElementById('pdf-container');
        if (!container) return;

        this.pdfh5 = new Pdfh5('#pdf-container', {
          pdfurl: url,
          renderType: 'canvas',
          lazyLoad: false,
          scrollEnable: true,
          zoomEnable: false,
          scale: 1.5,
          textLayer: true,
          cMapUrl: 'https://unpkg.com/pdfjs-dist@2.0.943/cmaps/',
          cMapPacked: true
        });

        this.pdfh5.on('complete', (status, msg, time) => {
          console.log('PDF加载完成，状态：' + status + '，总页数：' + this.pdfh5.totalNum);
          this.pdfh5.zoomEnable(false);
        });

        this.pdfh5.on('error', (msg, time) => {
          console.error('PDF加载错误：' + msg);
          this.error = 'PDF加载失败';
        });
      });
    },
    goBack() {
      this.$router.replace('/m/order')
    }
  }
};
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent
}

.m-report {
  --primary: #12b3a8;
  --primary-d: #0d9b91;
  --primary-l: #e8f8f6;
  --danger: #f5544b;
  --info: #2e7cf6;
  --t1: #14181f;
  --t2: #5b6472;
  --t3: #9aa4b2;
  --line: #eef0f4;
  --bg: #f4f6f8;
  --card: #fff;
  font-size: 14px;
  line-height: normal;
  -webkit-text-size-adjust: 100%;
  height: 100vh;
  background: var(--bg);
  display: flex;
  flex-direction: column;
  overflow: hidden
}

.navbar {
  height: 46px;
  flex: none;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border-bottom: .5px solid rgba(0, 0, 0, .15);
  z-index: 10
}

.navbar h1 {
  font-size: 17px;
  font-weight: 600;
  color: var(--t1)
}

.navbar .back {
  position: absolute;
  left: 4px;
  top: 0;
  height: 46px;
  width: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--t1);
  cursor: pointer;
  background: none;
  border: none
}

.navbar .back svg {
  width: 19px;
  height: 19px
}

/* 列表加载态 */
.m-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 90px 0;
  gap: 12px;
  color: var(--t3)
}

.m-loading .spin {
  width: 26px;
  height: 26px;
  border: 2.5px solid #d8dde5;
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin .8s linear infinite
}

.m-loading p {
  font-size: 13px;
  color: var(--t3);
  margin: 0
}

@keyframes spin {
  to {
    transform: rotate(360deg)
  }
}

/* 错误状态 */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 90px 20px;
  gap: 16px
}

.error-state p {
  font-size: 14px;
  color: var(--t3);
  margin: 0
}

/* PDF容器 */
.pdf-container {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #525659
}
</style>



// WEBPACK FOOTER //
// src/components/mobile/MReport.vue